/* The Brightivity frame: sidebar + top bar + content (UI kit § 06). */
import { useEffect, useRef, useState } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { Avatar, CreditsPill, Icon, IconButton, NavItem, Skeleton } from '../ui/index.jsx';
import { initialsFor, useSession } from './session.jsx';
import { useWallet } from './wallet.jsx';
import { APP_VERSION } from '../config.js';
import PrintJobToast, { PrintNotices } from '../features/print/PrintJobToast.jsx';
import { useOrgQueue } from '../contexts/OrgQueueContext.jsx';
import { SupportModal } from '../features/settings/settings.jsx';
import { useTour } from '../contexts/TourContext.jsx';
import TourHost from '../components/tour/TourHost.jsx';
import PrintRatingToast from '../features/print/PrintRatingToast.jsx';
import './shell.css';
import { useTranslation } from 'react-i18next';

/* The sidebar: B mark, 9 tools, Supplies pinned to the bottom (handoff order). */
export const TOOLS = [
  { path: '/', icon: 'home', label: 'nav.home' },
  { path: '/print', icon: 'print', label: 'nav.print' },
  { path: '/scan', icon: 'scan', label: 'nav.scan' },
  { path: '/design', icon: 'design', label: 'nav.design' },
  { path: '/browser', icon: 'canva', label: 'nav.browser' },
  { path: '/library', icon: 'library', label: 'nav.library' },
  { path: '/printables', icon: 'printables', label: 'nav.printables' },
  { path: '/resources', icon: 'resources', label: 'nav.resources' },
  { path: '/school', icon: 'school', label: 'nav.school' },
];
export const SUPPLY_STORE_URL = 'https://posterstudioexpress.com/poster-maker-printers-for-schools/poster-maker-supplies/';

function Sidebar() {
  const { t } = useTranslation('bv');
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { session } = useSession();
  const { pendingCount } = useOrgQueue();
  // Desktop LeftSideNavbar: My School is the org-admin console, shown only to approvers (with the waiting count).
  const tools = TOOLS.filter((t) => t.path !== '/school' || (session && session.orgAdmin));
  return (
    <nav className="bv-sidebar" aria-label={t('nav.tools')}>
      <Link to="/" className="bv-sidebar__logo" aria-label={t('nav.homeLink')}>
        <img src="/brand/app-icon-180.png" alt="" width="58" height="58" />
      </Link>
      <div className="bv-sidebar__tools">
        {tools.map((tl) => (
          // NavLink sets aria-current="page" on the active tool → yellow square (components.css).
          <NavLink key={tl.path} to={tl.path} end={tl.path === '/'} className="bv-navitem" onClick={(e) => {
            // The Design editor's "Save your poster?" guard (window.__pseUnsavedGuard) is asked first.
            const g = window.__pseUnsavedGuard;
            if (g && g.isDirty() && pathname !== tl.path) { e.preventDefault(); g.request(() => navigate(tl.path)); }
          }}>
            <span className="bv-navitem__box"><Icon name={tl.icon} size={24} />
              {tl.path === '/school' && pendingCount > 0 && <span className="bv-navitem__badge" data-testid="school-badge" aria-label={t('nav.waiting', { count: pendingCount })}>{pendingCount > 99 ? '99+' : pendingCount}</span>}
            </span>
            <span>{t(tl.label)}</span>
          </NavLink>
        ))}
      </div>
      <div className="bv-sidebar__bottom">
        <NavItem as="a" href={SUPPLY_STORE_URL} target="_blank" rel="noopener" icon="supplies" label={t('nav.supplies')} />
      </div>
    </nav>
  );
}

function ProfileMenu({ onClose }) {
  const { t } = useTranslation('bv');
  const { session, signOut } = useSession();
  const navigate = useNavigate();
  const ref = useRef(null);
  useEffect(() => {
    const away = (e) => { if (ref.current && !ref.current.contains(e.target)) onClose(); };
    const esc = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('mousedown', away);
    document.addEventListener('keydown', esc);
    return () => { document.removeEventListener('mousedown', away); document.removeEventListener('keydown', esc); };
  }, [onClose]);
  return (
    <div className="bv-profile-menu" ref={ref} role="menu">
      <div className="bv-profile-menu__who">
        <div className="bv-profile-menu__name">{(session && session.name) || t('shell.yourAccount')}</div>
        <div className="bv-profile-menu__email">{session && session.email}</div>
      </div>
      <button type="button" role="menuitem" onClick={() => { onClose(); navigate('/settings'); }}><Icon name="gear" size={20} />{t('shell.settings')}</button>
      <button type="button" role="menuitem" onClick={() => { onClose(); navigate('/settings?open=history'); }}><Icon name="print" size={20} />{t('shell.printHistory')}</button>
      <button type="button" role="menuitem" onClick={() => { onClose(); window.dispatchEvent(new Event('pse-start-tour')); }} data-testid="menu-tour"><Icon name="sparkle" size={20} />{t('shell.takeTour')}</button>
      <a role="menuitem" href={SUPPLY_STORE_URL} target="_blank" rel="noopener"><Icon name="supplies" size={20} />{t('shell.supplyStore')}</a>
      <button type="button" role="menuitem" onClick={() => { signOut(); navigate('/sign-in'); }}><Icon name="arrow" size={20} />{t('shell.signOut')}</button>
    </div>
  );
}

/** Top bar: title + subtitle (or the long logo on Home), credits, settings, profile. */
export function TopBar({ title, subtitle, logo, actions }) {
  const { t } = useTranslation('bv');
  const { balance, openCredits } = useWallet();
  const { session } = useSession();
  const navigate = useNavigate();
  const [menu, setMenu] = useState(false);
  return (
    <header className="bv-topbar">
      <div>
        {logo ? <img className="bv-topbar__logo" src="/brand/logo-long-on-light.svg" alt="Brightivity" /> : (
          <>
            <h1 className="bv-topbar__title">{title}</h1>
            {subtitle && <p className="bv-topbar__sub">{subtitle}</p>}
          </>
        )}
      </div>
      <div className="bv-topbar__right">
        {actions}
        {balance == null ? <Skeleton width={150} height={46} radius={999} /> : <CreditsPill credits={balance} onClick={openCredits} data-testid="credits-pill" />}
        <IconButton icon="gear" label={t('shell.settings')} onClick={() => navigate('/settings')} />
        <Avatar initials={initialsFor(session)} aria-haspopup="menu" aria-expanded={menu} onClick={() => setMenu((m) => !m)} />
        {menu && <ProfileMenu onClose={() => setMenu(false)} />}
      </div>
    </header>
  );
}

// Desktop HeroSection footer: the same Poster Studio Express pages + social links; Support opens the ticket form.
const FOOTER_LINKS = [
  { label: 'footer.supplyStore', url: 'https://posterstudioexpress.com/software-supply-store/' },
  { label: 'footer.registerPrinter', url: 'https://posterstudioexpress.com/register-your-printer/' },
  { label: 'footer.support', support: true },
  { label: 'footer.referFriend', url: 'https://posterstudioexpress.com/refer-a-friend-program/' },
  { label: 'footer.scheduleInstall', url: 'https://posterstudioexpress.com/schedule-poster-maker-install/' },
];
const SOCIAL = [
  { name: 'Facebook', icon: 'facebook', url: 'https://www.facebook.com/PosterStudioExpress' },
  { name: 'X', icon: 'x', url: 'https://x.com/posterstudiox' },
  { name: 'Instagram', icon: 'instagram', url: 'https://www.instagram.com/posterstudioexpress/' },
  { name: 'TikTok', icon: 'tiktok', url: 'https://www.tiktok.com/@posterstudioexpress' },
  { name: 'YouTube', icon: 'youtube', url: 'https://www.youtube.com/@PosterStudioExpress' },
  { name: 'Pinterest', icon: 'pinterest', url: 'https://www.pinterest.com/posterstudioexpress/' },
  { name: 'LinkedIn', icon: 'linkedin', url: 'https://www.linkedin.com/company/poster-studio-express/' },
];

export function Footer() {
  const { t } = useTranslation('bv');
  const [support, setSupport] = useState(false);
  return (
    <footer className="bv-footer" data-testid="footer">
      <nav className="bv-footer__links" aria-label={t('footer.more')}>
        {FOOTER_LINKS.map((l) => (l.support
          ? <button key={l.label} type="button" className="bv-footer__btn" onClick={() => setSupport(true)} data-testid="footer-support">{t(l.label)}</button>
          : <a key={l.label} href={l.url} target="_blank" rel="noopener">{t(l.label)}</a>))}
      </nav>
      <div className="bv-footer__social" aria-label={t('footer.follow')}>
        {SOCIAL.map((x) => <a key={x.name} href={x.url} target="_blank" rel="noopener" aria-label={x.name} title={x.name}><img src={`/social/${x.icon}.png`} alt="" width="22" height="22" /></a>)}
      </div>
      <div className="bv-footer__meta">{t('footer.from', { version: APP_VERSION })}</div>
      <SupportModal open={support} onClose={() => setSupport(false)} />
    </footer>
  );
}

export default function AppShell({ children }) {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  // The first-run tour question waits until the user is inside the app (signed in, profile done) — desktop App.jsx.
  const { setDeferPrompt } = useTour();
  useEffect(() => { setDeferPrompt(false); }, [setDeferPrompt]);
  return (
    <div className="bv-app">
      <Sidebar />
      <main className="bv-main">{children}</main>
      <PrintJobToast />
      <PrintNotices />
      <PrintRatingToast />
      <TourHost />
    </div>
  );
}
